<template>
  <div>
    <!-- Encabezado -->
    <div class="flex items-center justify-between mb-6">
      <button class="mr-2 p-3 text-white bg-teal-800 hover:bg-teal-900 rounded-lg transition-colors" @click="back()"
        title="Volver atrás">
        <i class="fa fa-arrow-left"></i>
      </button>
      <h1 class="text-2xl sm:text-3xl font-bold">Crear venta</h1>
    </div>

    <Head title="Crear venta" />

    <!-- Wizard Steps Header -->
    <div class="flex flex-col sm:flex-row items-center justify-center mb-8 gap-4 sm:gap-0">
      <div v-for="step in steps" :key="step.id" class="flex items-center">
        <div
          :class="['w-10 h-10 flex items-center justify-center rounded-full text-lg', currentStep === step.id ? 'bg-teal-700 text-white' : 'bg-gray-200 text-gray-600']">
          {{ step.id }}
        </div>
        <span class="ml-2 mr-4 font-semibold" :class="currentStep === step.id ? 'text-teal-700' : 'text-gray-500'">{{
          step.label }}</span>
        <span v-if="step.id < steps.length" class="hidden sm:block w-8 h-1 bg-gray-300 mx-1 rounded"></span>
      </div>
    </div>

    <!-- Paso 1: Cliente -->
    <div v-if="currentStep === 1">
      <div class="bg-gradient-to-br from-teal-50 to-white rounded-2xl shadow-lg border border-teal-100 mb-8">
        <div class="px-4 py-6 sm:px-8 sm:py-8">
          <div class="mb-8 text-center">
            <div class="flex justify-center mb-2">
              <i class="fa fa-user-plus text-4xl text-teal-600 bg-white rounded-full shadow p-4"></i>
            </div>
            <h2 class="text-xl sm:text-2xl font-bold text-teal-800">Selecciona o registra un cliente</h2>
            <p class="text-gray-500">Busca un cliente existente o crea uno nuevo para asociar a la venta.</p>
          </div>

          <!-- Resumen visual del cliente seleccionado -->
          <div v-if="form.client.name"
            class="mb-8 flex flex-col sm:flex-row items-center gap-4 bg-teal-50 border border-teal-100 rounded-lg p-4 shadow-sm">
            <i class="fa fa-user-circle text-3xl text-teal-500"></i>
            <div class="text-center sm:text-left">
              <div class="font-semibold text-teal-700">{{ form.client.name }}</div>
              <div class="text-gray-600 text-sm">
                <span v-if="form.client.identification">{{ form.client.identification }} &bull; </span>
                <span v-if="form.client.phone">{{ form.client.phone }} &bull; </span>
                <span v-if="form.client.address">{{ form.client.address }}</span>
              </div>
              <div class="text-xs text-gray-400">Deuda: ${{ form.client.balance }}</div>
            </div>
          </div>

          <find-or-create-client @addClient="takeClient" />

          <!-- Formulario de cliente -->
          <transition name="fade">
            <div v-if="showClientCreate"
              class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mt-8 bg-white border border-teal-100 rounded-xl p-4 md:p-6 shadow">
              <text-input v-model="form.client.name" label="Nombre y Apellido" placeholder="Ingrese el nombre completo"
                class="mb-2" input-class="focus:ring-teal-500 focus:border-teal-500" />
              <text-input v-model="form.client.identification" label="Alias o Cédula"
                placeholder="Ingrese identificación" class="mb-2"
                input-class="focus:ring-teal-500 focus:border-teal-500" />
              <text-input v-model="form.client.phone" label="Teléfono" placeholder="Ingrese número de contacto"
                class="mb-2" input-class="focus:ring-teal-500 focus:border-teal-500" />
              <text-input v-model="form.client.address" label="Dirección" placeholder="Ingrese dirección completa"
                class="mb-2" input-class="focus:ring-teal-500 focus:border-teal-500" />
              <text-input v-model="form.client.balance" label="Monto adeudado" disabled class="mb-2"
                input-class="bg-gray-100 text-gray-500" />
            </div>
          </transition>
        </div>
      </div>
      <div class="flex justify-end mt-4">
        <button
          class="btn-blue w-full sm:w-auto px-8 py-4 text-lg shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
          :disabled="!canGoToStep(2)" @click="tryNextStep(2)">
          Siguiente <i class="fa fa-arrow-right ml-2"></i>
        </button>
      </div>
      <div v-if="stepWarning && currentStep === 1" class="text-red-600 text-sm mt-2 text-center">
        {{ stepWarning }}
      </div>
    </div>

    <!-- Paso 2: Productos y Descuento -->
    <div v-if="currentStep === 2">
      <!-- Encabezado visual -->
      <div class="mb-6 text-center">
        <h2 class="text-xl sm:text-2xl font-bold text-teal-800 mb-1 flex items-center justify-center gap-2">
          <i class="fa fa-shopping-cart text-teal-500"></i> Selecciona productos, servicios o aplica descuento
        </h2>
        <p class="text-gray-500">Arma el carrito, agrega servicios rápidos y ajusta el descuento para tu cliente.</p>
      </div>

      <!-- Acciones principales -->
      <div class="flex flex-col sm:flex-row gap-4 mb-4">
        <button class="flex-1 btn-blue flex items-center justify-center gap-2 py-4 text-lg shadow-md"
          @click="listProducts = !listProducts" title="Ver lista de productos">
          <i class="fa fa-list"></i>
          Lista de productos
        </button>
        <button class="flex-1 btn-blue flex items-center justify-center gap-2 py-4 text-lg shadow-md"
          @click="addQuickProduct()" title="Agregar servicio o producto rápido">
          <i class="fa fa-plus"></i>
          Servicio o producto rápido
        </button>
      </div>

      <!-- Barra de búsqueda -->
      <div class="mb-6">
        <text-input v-model="form.search.terms" placeholder="Buscar productos..."
          class="w-full text-lg border-2 border-teal-200 rounded-lg shadow-sm"
          input-class="focus:ring-teal-500 focus:border-teal-500">
          <template #append>
            <i class="fa fa-search text-teal-400"></i>
          </template>
        </text-input>
      </div>

      <!-- Lista de productos tipo tarjeta/flotante -->
      <transition name="fade">
        <div v-if="listProducts" class="mb-8">
          <div class="bg-white border border-teal-100 rounded-2xl shadow-xl max-h-80 overflow-y-auto p-4">
            <!-- Vista móvil de productos -->
            <div class="sm:hidden space-y-4">
              <div v-for="product in products.data" :key="product.id"
                class="p-4 border border-teal-100 rounded-lg hover:bg-teal-50 cursor-pointer transition"
                @click="selectProduct(product)">
                <div class="flex justify-between items-start mb-2">
                  <span class="font-mono text-sm text-gray-500">{{ product.code }}</span>
                  <span class="text-teal-700 font-semibold">${{ product.prices[0].price }}</span>
                </div>
                <h3 class="font-semibold mb-1">
                  <i v-if="product.featured" class="fa fa-star text-yellow-400 mr-1" title="Producto Favorito"></i>
                  {{ product.name }}
                </h3>
                <div class="text-sm text-gray-500">
                  Disponibles: {{ product.type_product == 'product' ? product.inventory?.quantity : 'N/A' }}
                </div>
              </div>
            </div>
            <!-- Vista desktop de productos -->
            <table class="hidden sm:table w-full text-base">
              <thead class="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th class="p-3 text-left">Código</th>
                  <th class="p-3 text-left">Producto</th>
                  <th class="p-3 text-center">Disponibles</th>
                  <th class="p-3 text-right">Precio</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="product in products.data" :key="product.id"
                  class="hover:bg-teal-50 cursor-pointer border-t transition" @click="selectProduct(product)">
                  <td class="p-3 font-mono">{{ product.code }}</td>
                  <td class="p-3">
                    <i v-if="product.featured" class="fa fa-star text-yellow-400 mr-2" title="Producto Favorito"></i>
                    {{ product.name }}
                  </td>
                  <td class="p-3 text-center">{{ product.type_product == 'product' ? product.inventory?.quantity : 'N/A'
                  }}
                  </td>
                  <td class="p-3 text-right text-teal-700 font-semibold">${{ product.prices[0].price }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </transition>

      <!-- Carrito de compras -->
      <div class="bg-white rounded-xl shadow-md border border-teal-100 mb-8">
        <!-- Vista móvil del carrito -->
        <div class="sm:hidden divide-y divide-gray-200">
          <div v-for="(product, index) in form.products" :key="product.id" class="p-4">
            <div class="flex justify-between items-start mb-2">
              <div>
                <span v-if="!product.quick" class="font-mono text-sm text-gray-500">{{ product.code }}</span>
                <text-input v-else v-model="form.products[index].code" placeholder="Código" class="w-full mb-2"
                  input-class="focus:ring-teal-500 focus:border-teal-500" />
              </div>
              <button @click="removeProduct(product)"
                class="p-2 text-white bg-red-500 hover:bg-red-600 rounded-full transition-colors shadow"
                title="Eliminar producto">
                <i class="fa fa-trash"></i>
              </button>
            </div>
            <div class="mb-2">
              <text-input v-if="product.quick" v-model="form.products[index].name" placeholder="Nombre"
                class="w-full mb-2" input-class="focus:ring-teal-500 focus:border-teal-500" />
              <span v-else class="font-semibold">{{ product.name }}</span>
            </div>
            <div class="grid grid-cols-2 gap-2 mb-2">
              <div>
                <label class="text-sm text-gray-500">Cantidad</label>
                <text-input v-model="form.products[index].quantity" type="number"
                  :max="product.stock !== 'N/A' ? product.stock : null" class="w-full"
                  input-class="focus:ring-teal-500 focus:border-teal-500" />
              </div>
              <div>
                <label class="text-sm text-gray-500">Precio</label>
                <text-input v-model="form.products[index].price" type="number"
                  :disabled="!product.quick && auth.user.role == 'sales' && !(account && account.sellerCanModifyPrices)" class="w-full"
                  input-class="focus:ring-teal-500 focus:border-teal-500" />
              </div>
            </div>
            <div v-if="product.type_product === 'service' || product.quick" class="mb-2">
              <textarea v-model="form.products[index].description" rows="2"
                class="form-textarea w-full rounded-lg border-gray-300 text-sm focus:ring-teal-500 focus:border-teal-500"
                placeholder="Describe el servicio realizado, detalles, condiciones, etc."></textarea>
            </div>
            <div class="text-sm text-gray-600 mb-1">
              <span v-if="product.type_product === 'service' && form.products[index].fee > 0" class="text-blue-600">
                Fee: ${{ form.products[index].fee }}
              </span>
            </div>
            <div class="text-right font-semibold text-teal-700">
              Total: ${{ (form.products[index].price + form.products[index].fee) * product.quantity }}
            </div>
          </div>
        </div>
        <!-- Vista desktop del carrito -->
        <table class="hidden sm:table w-full text-base">
          <thead class="bg-teal-50">
            <tr>
              <th class="p-3 text-left">Código</th>
              <th class="p-3 text-left">Producto</th>
              <th class="p-3 text-center">Disponible</th>
              <th class="p-3 text-center">Cantidad</th>
              <th class="p-3 text-right">Precio</th>
              <th class="p-3 text-right">Fee</th>
              <th class="p-3 text-right">Total</th>
              <th class="p-3"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(product, index) in form.products" :key="product.id"
              class="border-t hover:bg-gray-50 transition">
              <td class="p-3">
                <text-input v-if="product.quick" v-model="form.products[index].code" placeholder="Código" class="w-full"
                  input-class="focus:ring-teal-500 focus:border-teal-500" />
                <span v-else class="font-mono">{{ product.code }}</span>
              </td>
              <td class="p-3 align-top">
                <div>
                  <text-input v-if="product.quick" v-model="form.products[index].name" placeholder="Nombre"
                    class="w-full mb-1" input-class="focus:ring-teal-500 focus:border-teal-500" />
                  <span v-else>{{ product.name }}</span>
                  <!-- Descripción para servicios -->
                  <div v-if="product.type_product === 'service' || product.quick">
                    <textarea v-model="form.products[index].description" rows="2"
                      class="form-textarea w-full rounded-lg border-gray-300 mt-2 text-sm focus:ring-teal-500 focus:border-teal-500"
                      placeholder="Describe el servicio realizado, detalles, condiciones, etc."></textarea>
                  </div>
                  <div v-else-if="product.description" class="text-xs text-gray-500 mt-1">{{ product.description }}
                  </div>
                </div>
              </td>
              <td class="p-3 text-center">{{ product.stock }}</td>
              <td class="p-3">
                <text-input v-model="form.products[index].quantity" type="number"
                  :max="product.stock !== 'N/A' ? product.stock : null" class="w-20 text-center"
                  input-class="focus:ring-teal-500 focus:border-teal-500" />
              </td>
              <td class="p-3">
                <text-input v-model="form.products[index].price" type="number"
                  :disabled="!product.quick && auth.user.role == 'sales' && !(account && account.sellerCanModifyPrices)" class="w-24 text-right"
                  input-class="focus:ring-teal-500 focus:border-teal-500" />
              </td>
              <td class="p-3">
                <text-input v-model="form.products[index].fee" type="number"
                  :disabled="!product.quick && auth.user.role == 'sales'" class="w-24 text-right"
                  input-class="focus:ring-teal-500 focus:border-teal-500" />
              </td>
              <td class="p-3 text-right font-semibold text-teal-700">
                ${{ (form.products[index].price + form.products[index].fee) * product.quantity }}
              </td>
              <td class="p-3 text-center">
                <button @click="removeProduct(product)"
                  class="p-2 text-white bg-red-500 hover:bg-red-600 rounded-full transition-colors shadow"
                  title="Eliminar producto">
                  <i class="fa fa-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Resumen de totales y descuento -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <!-- Resumen de totales -->
        <div class="bg-teal-50 border border-teal-100 rounded-xl p-6 shadow flex flex-col gap-2 mb-2 md:mb-0">
          <div class="flex justify-between items-center">
            <span class="font-semibold text-teal-700">Subtotal:</span>
            <span class="font-medium">${{ getSubtotal() }}</span>
          </div>
          <div v-if="getTotalFees() > 0" class="flex justify-between items-center text-blue-600">
            <span>Fees:</span>
            <span>+ ${{ getTotalFees() }}</span>
          </div>
          <div v-if="getDiscountAmount() > 0" class="flex justify-between items-center text-red-600">
            <span>Descuento <span v-if="form.discount.type === 'percentage'">({{ form.discount.value }}%)</span>:</span>
            <span>- ${{ getDiscountAmount() }}</span>
          </div>
          <div class="flex justify-between items-center font-bold text-xl mt-2">
            <span>Total:</span>
            <span class="text-teal-800">${{ getTotal() }}</span>
          </div>
          <div class="flex flex-col sm:flex-row items-center gap-2 mt-2">
            <span class="text-sm text-gray-500">Tasa de cambio:</span>
            <text-input v-model="form.change" type="number" step="0.01" min="0" class="w-24"
              input-class="focus:ring-teal-500 focus:border-teal-500 text-right" />
            <span class="text-sm text-gray-500">Bs. {{ (getTotal() * form.change).toFixed(2) }}</span>
          </div>
        </div>
        <!-- Tarjeta de descuento -->
        <div class="bg-white border border-teal-100 rounded-xl p-6 shadow flex flex-col gap-4">
          <div class="flex items-center gap-2 mb-2">
            <i class="fa fa-percent text-teal-500"></i>
            <span class="font-semibold text-teal-700">Aplicar descuento</span>
          </div>
          <div class="flex flex-col gap-2">
            <label class="block text-gray-700">Tipo de descuento</label>
            <select v-model="form.discount.type"
              class="form-select rounded-lg border-gray-300 focus:ring-teal-500 focus:border-teal-500">
              <option value="percentage">Porcentaje (%)</option>
              <option value="amount">Monto fijo ($)</option>
            </select>
          </div>
          <text-input v-model="form.discount.value"
            :label="form.discount.type === 'percentage' ? 'Porcentaje (%)' : 'Monto ($)'" type="number"
            :step="form.discount.type === 'percentage' ? '1' : '0.01'" :min="0"
            :max="form.discount.type === 'percentage' ? 100 : null" class="w-full"
            input-class="focus:ring-teal-500 focus:border-teal-500" />
          <div class="text-sm text-gray-600">
            <span class="font-medium">Descuento aplicado:</span>
            <span class="text-red-600 font-bold">${{ getDiscountAmount() }}</span>
          </div>
        </div>
      </div>

      <!-- Switch de crédito -->
      <div class="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
        <label class="inline-flex items-center cursor-pointer w-full">
          <input v-model="form.isCredit" type="checkbox" class="form-checkbox h-6 w-6 text-blue-600">
          <span class="ml-3 text-lg font-semibold text-blue-800">Venta a crédito</span>
        </label>
      </div>

      <div class="flex flex-col sm:flex-row justify-between gap-4 mt-4">
        <button class="btn-blue px-8 py-4 text-lg w-full sm:w-auto" @click="prevStep">
          <i class="fa fa-arrow-left mr-2"></i> Anterior
        </button>
        <button class="btn-blue px-8 py-4 text-lg w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
          :disabled="!canGoToStep(3)" @click="tryNextStep(3)">
          Siguiente <i class="fa fa-arrow-right ml-2"></i>
        </button>
      </div>
      <div v-if="stepWarning && currentStep === 2" class="text-red-600 text-sm mt-2 text-center">
        {{ stepWarning }}
      </div>
    </div>

    <!-- Paso 3: Observaciones y Previsualización de la Orden -->
    <div v-if="currentStep === 3">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <!-- Cliente -->
        <div class="flex flex-col gap-4 bg-white rounded-xl shadow p-4 border border-teal-100">
          <div class="flex items-center gap-2 mb-2">
            <i class="fa fa-user-circle text-2xl text-teal-500"></i>
            <span class="font-semibold text-teal-800 text-lg">Cliente</span>
          </div>
          <div class="flex items-center gap-2">
            <i class="fa fa-id-badge text-gray-400"></i>
            <span class="font-semibold">{{ form.client.name }}</span>
          </div>
          <div class="flex items-center gap-2">
            <i class="fa fa-address-card text-gray-400"></i>
            <span class="text-gray-800 text-sm">{{ form.client.identification }}</span>
          </div>
          <div class="flex items-center gap-2">
            <i class="fa fa-envelope text-gray-400"></i>
            <span class="text-gray-800 text-sm">{{ form.client.email || '-' }}</span>
          </div>
          <div class="flex items-center gap-2">
            <i class="fa fa-phone text-gray-400"></i>
            <span class="text-gray-800 text-sm">{{ form.client.phone || '-' }}</span>
          </div>
        </div>
        <!-- Productos y servicios -->
        <div class="col-span-1 md:col-span-2 flex flex-col gap-4 bg-white rounded-xl shadow p-4 border border-teal-100">
          <div class="flex items-center gap-2 mb-2">
            <i class="fa fa-boxes text-2xl text-teal-500"></i>
            <span class="font-semibold text-teal-800 text-lg">Productos y Servicios</span>
          </div>
          <!-- Vista móvil de productos -->
          <div class="sm:hidden space-y-4">
            <div v-for="(product, idx) in form.products" :key="idx" class="border-b pb-4">
              <div class="flex justify-between items-start mb-1">
                <h3 class="font-semibold">{{ product.name }}</h3>
                <span class="text-teal-700 font-bold">${{ (product.price + product.fee) * product.quantity }}</span>
              </div>
              <div class="text-sm text-gray-600 mb-1">{{ product.description || '-' }}</div>
              <div class="flex justify-between text-sm">
                <span>Cantidad: {{ product.quantity }}</span>
                <span>Precio: ${{ Number(product.price).toFixed(2) }}</span>
              </div>
            </div>
          </div>
          <!-- Vista desktop de productos -->
          <div class="hidden sm:block overflow-x-auto">
            <table class="w-full text-sm">
              <thead class="bg-gray-50">
                <tr>
                  <th class="p-2 text-left">Producto/Servicio</th>
                  <th class="p-2 text-left">Descripción</th>
                  <th class="p-2 text-center">Cantidad</th>
                  <th class="p-2 text-right">Precio</th>
                  <th class="p-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(product, idx) in form.products" :key="idx" class="border-b">
                  <td class="p-2">{{ product.name }}</td>
                  <td class="p-2 text-xs text-gray-600">{{ product.description || '-' }}</td>
                  <td class="p-2 text-center">{{ product.quantity }}</td>
                  <td class="p-2 text-right">${{ Number(product.price).toFixed(2) }}</td>
                  <td class="p-2 text-right">${{ (product.price * product.quantity).toFixed(2) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="flex flex-col sm:flex-row gap-4 justify-between mt-4">
            <div class="flex items-center gap-2">
              <i class="fa fa-tag text-gray-400"></i>
              <span class="font-semibold">Descuento:</span>
              <span v-if="form.discount && form.discount.value">{{ form.discount.type === 'amount' ? ('$' +
                Number(form.discount.value).toFixed(2)) : (Number(form.discount.value) + '%') }}</span>
              <span v-else>-</span>
            </div>
            <div class="flex items-center gap-2">
              <i class="fa fa-coins text-gray-400"></i>
              <span class="font-semibold">Total estimado:</span>
              <span class="text-teal-700 text-lg font-bold">
                ${{ getTotal() }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <!-- Observaciones y controles -->
      <div class="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        <div class="p-6 space-y-6">
          <text-input v-model="form.date" label="Fecha y hora" type="datetime-local" required />
          <text-input v-model="form.change" label="Tasa de cambio" type="number" step="0.01" />
          <text-area-input v-model="form.comentary" label="Observaciones"
            placeholder="Ingrese observaciones importantes" required />
        </div>
      </div>
      <!-- Campos adicionales -->
      <div v-if="hasOrderAdditionals" class="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        <div class="p-6 space-y-6">
          <h3 class="text-lg font-semibold text-gray-800 mb-4">Campos adicionales</h3>

          <!-- Campos para órdenes -->
          <div v-if="hasOrderAdditionals" class="space-y-4">
            <h4 class="font-medium text-gray-700">Para la orden</h4>
            <div v-for="additional in additionals" :key="additional.id" class="space-y-2">
              <label :for="`additional-${additional.id}`" class="block text-sm font-medium text-gray-700">
                {{ additional.field_label }}
                <span v-if="additional.is_required" class="text-red-500">*</span>
              </label>

              <!-- Campo de texto -->
              <input v-if="additional.field_type === 'text'" :id="`additional-${additional.id}`"
                v-model="form.additional_values[additional.id]" type="text" :required="additional.is_required"
                class="form-input w-full rounded-lg border-gray-300 focus:ring-teal-500 focus:border-teal-500"
                :placeholder="`Ingrese ${additional.field_label.toLowerCase()}`" />

              <!-- Campo de número -->
              <input v-if="additional.field_type === 'number'" :id="`additional-${additional.id}`"
                v-model="form.additional_values[additional.id]" type="number" :required="additional.is_required"
                class="form-input w-full rounded-lg border-gray-300 focus:ring-teal-500 focus:border-teal-500"
                :placeholder="`Ingrese ${additional.field_label.toLowerCase()}`" />

              <!-- Campo de fecha -->
              <input v-if="additional.field_type === 'date'" :id="`additional-${additional.id}`"
                v-model="form.additional_values[additional.id]" type="date" :required="additional.is_required"
                class="form-input w-full rounded-lg border-gray-300 focus:ring-teal-500 focus:border-teal-500" />

              <!-- Campo de área de texto -->
              <textarea v-if="additional.field_type === 'textarea'" :id="`additional-${additional.id}`"
                v-model="form.additional_values[additional.id]" rows="3" :required="additional.is_required"
                class="form-textarea w-full rounded-lg border-gray-300 focus:ring-teal-500 focus:border-teal-500"
                :placeholder="`Ingrese ${additional.field_label.toLowerCase()}`"></textarea>

              <!-- Campo de selección -->
              <select v-if="additional.field_type === 'select'" :id="`additional-${additional.id}`"
                v-model="form.additional_values[additional.id]" :required="additional.is_required"
                class="form-select w-full rounded-lg border-gray-300 focus:ring-teal-500 focus:border-teal-500">
                <option value="">Seleccione una opción</option>
                <option v-for="option in (additional.field_options || [])" :key="option" :value="option">
                  {{ option }}
                </option>
              </select>

              <!-- Campo booleano -->
              <div v-if="additional.field_type === 'boolean'" class="flex items-center space-x-3">
                <input :id="`additional-${additional.id}-yes`" v-model="form.additional_values[additional.id]"
                  type="radio" :name="`additional-${additional.id}`" :value="true" :required="additional.is_required"
                  class="form-radio text-teal-600 focus:ring-teal-500" />
                <label :for="`additional-${additional.id}-yes`" class="text-sm text-gray-700">Sí</label>

                <input :id="`additional-${additional.id}-no`" v-model="form.additional_values[additional.id]"
                  type="radio" :name="`additional-${additional.id}`" :value="false" :required="additional.is_required"
                  class="form-radio text-teal-600 focus:ring-teal-500" />
                <label :for="`additional-id-${additional.id}-no`" class="text-sm text-gray-700">No</label>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-col sm:flex-row justify-between gap-4">
        <button class="btn-blue px-8 py-4 text-lg w-full sm:w-auto" @click="prevStep">
          <i class="fa fa-arrow-left mr-2"></i> Anterior
        </button>
        <button @click="generateOrder" class="btn-blue text-lg px-8 py-4 w-full sm:w-auto">
          <i class="fa fa-check mr-2"></i>
          Generar Venta
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Layout from '../../Shared/Layout.vue'
import TextInput from '@/Shared/TextInput'
import pickBy from 'lodash/pickBy'
import { Head } from '@inertiajs/inertia-vue3'
import SelectInput from '@/Shared/SelectInput.vue'
import FindOrCreateClient from '../../Shared/FindOrCreateClient.vue'
import TextAreaInput from '@/Shared/TextAreaInput'

export default {
  components: {
    SelectInput,
    TextInput,
    Head,
    FindOrCreateClient,
    TextAreaInput
  },

  layout: Layout,

  props: {
    products: Object,
    client: Object,
    brands: Object,
    parts: Object,
    models: Object,
    deposits: Object,
    regions: Object,
    change: Number,
    auth: Object,
    additionals: Object,
    account: Object
  },

  data() {
    return {
      stepWarning: '',
      currentStep: 1,
      steps: [
        { id: 1, label: 'Cliente' },
        { id: 2, label: 'Productos y Descuento' },
        { id: 3, label: 'Observaciones' },
      ],
      getQuantity(inventory) {
        return inventory.reduce((total, inv) => total + inv.quantity, 0)
      },

      getTotal() {
        return this.form.products.reduce((total, product) => {
          return total + (product.price + product.fee) * product.quantity
        }, 0).toFixed(2)
      },

      selected: null,
      options: ['list', 'of', 'options'],

      form: this.$inertia.form({
        date: '',
        search: {
          terms: '',
          deposit: '',
          brand_id: '',
          part_id: '',
          truck_model_id: '',
          region_id: '',
        },
        isCredit: false,
        number_order: null,
        client: {
          name: '',
          email: '',
          cedula: '',
          telefono: '',
          address: '',
          identification: '',
          balance: 0,
          typeClient: '',
        },
        discount: {
          type: 'percentage', // 'percentage' or 'amount'
          value: 0
        },
        comentary: '',
        products: [],
        method: '',
        referencePay: '',
        change: this.change,
        estimate: true,
        order_buy: '',
        additional_values: {}
      }),

      listProducts: false,
      showClientCreate: false,
      textSearch: 'Buscar cliente',
    }
  },

  computed: {
    hasOrderAdditionals() {
      return this.additionals && this.additionals.length > 0;
    },

    hasClientAdditionals() {
      return this.additionals && this.additionals.length > 0;
    },

    orderAdditionals() {
      return this.additionals?.order || [];
    },

    clientAdditionals() {
      return this.additionals?.client || [];
    }
  },

  watch: {
    'form.search.terms': 'onSearchProductsChange',
    'form.search.deposit': 'onSearchProductsChange',
    'form.search.brand_id': 'onSearchProductsChange',
    'form.search.part_id': 'onSearchProductsChange',
    'form.search.truck_model_id': 'onSearchProductsChange',
    'form.search.region_id': 'onSearchProductsChange',

    // Watcher para campos adicionales
    additionals: {
      handler() {
        this.initializeAdditionalFields()
      },
      deep: true,
      immediate: false
    }
  },

  mounted() {
    this.form.date = new Date().toISOString().substr(0, 10)
    this.initializeAdditionalFields()
  },

  methods: {
    getTotalFees() {
    return this.form.products.reduce((total, product) => {
      if (product.type_product === 'service' && product.fee) {
        return total + (parseFloat(product.fee) * parseFloat(product.quantity));
      }
      return total;
    }, 0).toFixed(2);
  },
    canGoToStep(step) {
      if (step === 2) {
        // Paso 1 → 2: cliente seleccionado
        return !!(this.form.client && this.form.client.name && this.form.client.identification);
      }
      if (step === 3) {
        // Paso 2 → 3: al menos un producto
        return this.form.products && this.form.products.length > 0;
      }
      return true;
    },
    tryNextStep(step) {
      this.stepWarning = '';
      if (!this.canGoToStep(step)) {
        if (step === 2) {
          this.stepWarning = 'Debes seleccionar o crear un cliente antes de continuar.';
        } else if (step === 3) {
          this.stepWarning = 'Debes agregar al menos un producto o servicio antes de continuar.';
        }
        return;
      }
      this.nextStep();
    },
    nextStep() {
      if (this.currentStep < this.steps.length) {
        this.currentStep++;
        this.stepWarning = '';
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
        this.stepWarning = '';
      }
    },
    takeClient(client) {
      this.form.client = client
    },

    getDeposits(deposits) {
      return deposits
    },

    getSubtotal() {
      return this.form.products.reduce((total, product) => {
        return total + (parseFloat(product.price) + parseFloat(product.fee)) * parseFloat(product.quantity)
      }, 0).toFixed(2)
    },

    getDiscountAmount() {
      const subtotal = parseFloat(this.getSubtotal());
      if (this.form.discount.type === 'percentage') {
        return ((subtotal * parseFloat(this.form.discount.value)) / 100).toFixed(2);
      } else {
        return Math.min(parseFloat(this.form.discount.value), subtotal).toFixed(2);
      }
    },

    getTotal() {
      const subtotal = parseFloat(this.getSubtotal());
      const discount = parseFloat(this.getDiscountAmount());
      return (subtotal - discount).toFixed(2);
    },

    back() {
      window.history.back()
    },

    addQuickProduct() {
      this.form.products.push({
        id: 'quick',
        code: '',
        name: '',
        description: '',
        deposit: '',
        quantity: 1,
        price: 0,
        type_product: 'service',
        stock: null,
        quick: true
      })
    },

    getProductStock(inventory, deposito) {
      const found = inventory.find(inv => inv.deposit.id == deposito)
      return found ? found.quantity : ''
    },

    selectProduct(product) {
      if (product.type_product === 'product' && product?.inventory?.quantity <= 0) {
        return alert('No hay stock disponible de este producto')
      }

      const exists = this.form.products.some(p => p.id === product.id)

      if (!exists) {
        const stock = product.type_product === 'product' ? product.inventory.quantity : 'N/A';
        const price = product?.prices?.[0]?.price || product.price || 0;

        this.form.products.push({
          ...product,
          stock,
          quantity: 1,
          price,
          quick: false,
          fee: 0
        })
      }
    },

    removeProduct(product) {
      if (confirm('¿Está seguro que desea eliminar este producto?')) {
        this.form.products = this.form.products.filter(p => p.id !== product.id)
      }
    },

    findClient() {
      this.$inertia.get('/orders/create', pickBy(this.form), { preserveState: true })
      this.textSearch = 'Buscando...'

      setTimeout(() => {
        if (this.client) {
          this.textSearch = 'Buscar cliente'
          this.showClientCreate = true
          this.form.client = {
            name: this.client.name,
            email: this.client.email,
            phone: this.client.phone,
            address: this.client.address,
            identification: this.client.identification,
            balance: this.client.balance,
            typeClient: this.client.typeClient,
            companyName: this.client.companyName,
          }
        } else {
          this.textSearch = 'Buscar cliente'
          alert('No se encontró el cliente. Puede crear uno nuevo en el formulario.')
          this.showClientCreate = true
        }
      }, 1500)
    },

    generateOrder() {
      // Validar campos adicionales requeridos
      if (this.hasOrderAdditionals) {
        const requiredFields = this.additionals.filter(additional => additional.is_required);
        const missingFields = requiredFields.filter(additional =>
          !this.form.additional_values[additional.id] ||
          this.form.additional_values[additional.id] === ''
        );

        if (missingFields.length > 0) {
          const fieldNames = missingFields.map(field => field.field_label).join(', ');
          alert(`Debe completar los siguientes campos requeridos: ${fieldNames}`);
          return;
        }
      }
      this.form.post('/orders')
    },

    onSearchProductsChange() {
      this.$inertia.get('/orders/create',
        pickBy({ search: this.form.search }),
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
        }
      )
    },

    onSelectedProduct(consultant) {
      console.log(consultant)
    },

    updateSelected(newSelected) {
      this.selectProduct = newSelected
    },

    initializeAdditionalFields() {
      // Inicializar campos adicionales para órdenes
      if (this.hasOrderAdditionals) {
        this.orderAdditionals.forEach(additional => {
          if (!this.form.additional_values[additional.id]) {
            this.form.additional_values[additional.id] = '';
          }
        });
      }

      // Inicializar campos adicionales para clientes
      if (this.hasClientAdditionals) {
        this.clientAdditionals.forEach(additional => {
          if (!this.form.client.additional_values[additional.id]) {
            this.form.client.additional_values[additional.id] = '';
          }
        });
      }
    },
  },
}
</script>
